import { useEffect, useMemo, useRef } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowLineDown,
  ArrowLineUp,
  ArrowRight,
  ArrowUUpLeft,
  ArrowUp,
  ArrowsOutCardinal,
  Check,
  FlipHorizontal,
  FlipVertical,
  LinkSimple,
  Magnet,
  Ruler,
  Warning,
} from "@phosphor-icons/react";
import { SNAP_MODES, fmtIn, sideLetter } from "@/lib/arrange3d";

const SEAM_COLOR = "#f59e0b";
const SEAM_BAD = "#ef4444";

// Small rounded label drawn to a canvas so it can float over the seam as a sprite.
const labelTexture = (text, bad = false) => {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 96;
  const g = c.getContext("2d");
  g.fillStyle = bad ? "rgba(69,10,10,0.94)" : "rgba(24,24,27,0.92)";
  const r = 24;
  g.beginPath();
  g.moveTo(r, 0);
  g.arcTo(256, 0, 256, 96, r);
  g.arcTo(256, 96, 0, 96, r);
  g.arcTo(0, 96, 0, 0, r);
  g.arcTo(0, 0, 256, 0, r);
  g.closePath();
  g.fill();
  g.strokeStyle = bad ? SEAM_BAD : SEAM_COLOR;
  g.lineWidth = 4;
  g.stroke();
  g.fillStyle = bad ? "#fecaca" : "#fde68a";
  g.font = "bold 44px ui-monospace, Menlo, monospace";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(bad ? `! ${text}` : text, 128, 50);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  return tex;
};

const SeamLabel = ({ position, text, bad }) => {
  const tex = useMemo(() => labelTexture(text, bad), [text, bad]);
  useEffect(() => () => tex.dispose(), [tex]);
  return (
    <sprite position={position} scale={[13, 4.9, 1]} renderOrder={20}>
      <spriteMaterial map={tex} transparent depthTest={false} />
    </sprite>
  );
};

// Amber joint line + length tag on every seam where two tops meet; red when flagged.
export const SeamMarkers = ({ seams, thick }) =>
  seams.map((s) => {
    const dx = s.to[0] - s.from[0];
    const dz = s.to[1] - s.from[1];
    const mx = (s.from[0] + s.to[0]) / 2;
    const mz = (s.from[1] + s.to[1]) / 2;
    const y = (s.lift || 0) + thick + 0.06;
    const ang = -Math.atan2(dz, dx);
    const bad = (s.issues || []).length > 0;
    return (
      <group key={s.id}>
        <mesh position={[mx, y, mz]} rotation={[0, ang, 0]} renderOrder={10}>
          <boxGeometry args={[s.len, 0.12, bad ? 0.7 : 0.45]} />
          <meshBasicMaterial color={bad ? SEAM_BAD : SEAM_COLOR} depthTest={false} />
        </mesh>
        <SeamLabel position={[mx, y + 4.2, mz]} text={fmtIn(s.len)} bad={bad} />
      </group>
    );
  });

// While a piece or the rotate dial is held, turns pointer moves into floor-plane hits
// and parks the orbit controls so the camera stays still.
export const DragLayer = ({ drag, controls, onMove, onEnd }) => {
  const { camera, gl } = useThree();
  const moveRef = useRef(onMove);
  const endRef = useRef(onEnd);
  moveRef.current = onMove;
  endRef.current = onEnd;
  useEffect(() => {
    if (!drag) return undefined;
    if (controls.current) controls.current.enabled = false;
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(drag.planeY || 0));
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    const move = (e) => {
      const r = gl.domElement.getBoundingClientRect();
      ndc.set(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -((e.clientY - r.top) / r.height) * 2 + 1,
      );
      ray.setFromCamera(ndc, camera);
      const ok = ray.ray.intersectPlane(plane, hit);
      // world inches per screen pixel at the orbit target — used for vertical (lift) drags
      const target = controls.current?.target || new THREE.Vector3();
      const dist = camera.position.distanceTo(target);
      const fov = ((camera.fov || 38) * Math.PI) / 180;
      const inchPerPx = (2 * dist * Math.tan(fov / 2)) / Math.max(1, r.height);
      moveRef.current(ok ? hit.x : NaN, ok ? hit.z : NaN, e, inchPerPx);
    };
    const up = () => endRef.current();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (controls.current) controls.current.enabled = true;
    };
  }, [drag, camera, gl, controls]);
  return null;
};

// Outline + rotate dial on the selected flat piece. Drag the dial (or its knob) to spin;
// drag the arrow above the centre to raise / lower the top.
export const SelectionMarker = ({
  piece,
  thick,
  editable,
  onRotateStart,
  onLiftStart,
}) => {
  const { gl } = useThree();
  const cx = (Number(piece.length_in) || 0) / 2;
  const cy = (Number(piece.width_in) || 0) / 2;
  const R = Math.max(8, Math.min(cx, cy) + 5);
  const rot = (-(Number(piece.rot) || 0) * Math.PI) / 180;
  const lift = Number(piece.lift) || 0;
  const outline = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const arr = [];
    (piece.points || []).forEach(([x, y]) => arr.push(x - cx, 0, y - cy));
    g.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
    return g;
  }, [piece.points, cx, cy]);
  useEffect(() => () => outline.dispose(), [outline]);
  const grab = (e) => {
    e.stopPropagation();
    gl.domElement.style.cursor = "grabbing";
    onRotateStart?.(e);
  };
  const grabLift = (e) => {
    e.stopPropagation();
    gl.domElement.style.cursor = "ns-resize";
    onLiftStart?.(e);
  };
  const hover = (e) => {
    e.stopPropagation();
    gl.domElement.style.cursor = "grab";
  };
  const hoverLift = (e) => {
    e.stopPropagation();
    gl.domElement.style.cursor = "ns-resize";
  };
  const leave = () => {
    gl.domElement.style.cursor = "";
  };
  return (
    <group
      position={[(piece.x || 0) + cx, lift + thick + 0.35, (piece.y || 0) + cy]}
      rotation={[0, rot, 0]}
    >
      <lineLoop geometry={outline}>
        <lineBasicMaterial color="#a5b4fc" />
      </lineLoop>
      {editable && (
        <>
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            onPointerDown={grab}
            onPointerOver={hover}
            onPointerOut={leave}
          >
            <ringGeometry args={[R - 1, R + 1, 96]} />
            <meshBasicMaterial
              color="#6d5df6"
              transparent
              opacity={0.55}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
          <mesh
            position={[R, 0, 0]}
            onPointerDown={grab}
            onPointerOver={hover}
            onPointerOut={leave}
          >
            <sphereGeometry args={[1.8, 16, 16]} />
            <meshBasicMaterial color="#e0e7ff" />
          </mesh>
          <group position={[0, 6, 0]}>
            <mesh
              position={[0, -2.5, 0]}
              onPointerDown={grabLift}
              onPointerOver={hoverLift}
              onPointerOut={leave}
            >
              <cylinderGeometry args={[0.35, 0.35, 5, 8]} />
              <meshBasicMaterial color="#c7d2fe" />
            </mesh>
            <mesh
              position={[0, 1.2, 0]}
              onPointerDown={grabLift}
              onPointerOver={hoverLift}
              onPointerOut={leave}
            >
              <coneGeometry args={[1.5, 3, 12]} />
              <meshBasicMaterial color="#e0e7ff" />
            </mesh>
          </group>
        </>
      )}
    </group>
  );
};

const Tb = ({ testId, title, onClick, on, children, className = "" }) => (
  <button
    type="button"
    data-testid={testId}
    title={title}
    onClick={onClick}
    className={`flex h-7 items-center gap-1 rounded-full border px-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors duration-150 ${
      on
        ? "border-indigo-400/60 bg-indigo-500 text-white"
        : "border-zinc-700 bg-zinc-900/70 text-zinc-300 hover:border-zinc-500 hover:text-white"
    } ${className}`}
  >
    {children}
  </button>
);

// Floating controls over the 3D canvas: snap mode, apply/reset, and the selected piece's
// rotate / flip / nudge (flat) or backsplash / waterfall / attach (upright) actions.
export const ArrangeToolbar = ({
  snapMode,
  onSnapMode,
  piece,
  pieceName,
  attachName,
  connected,
  seams = [],
  hasOverrides,
  onRotate,
  onSetRot,
  onFlip,
  onNudge,
  onLift,
  onSetLift,
  onResetPiece,
  onStand,
  onDetach,
  onApply,
  onResetAll,
}) => {
  const upright = piece?.orientation === "vertical";
  return (
    <div
      data-testid="arrange-toolbar"
      className="absolute left-3 top-3 z-20 flex max-w-[calc(100%-24px)] flex-col gap-2"
    >
      <div className="flex flex-wrap items-center gap-1.5 rounded-2xl border border-zinc-800 bg-zinc-950/85 p-2 shadow-xl backdrop-blur">
        <span className="flex items-center gap-1.5 pl-1 pr-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          <ArrowsOutCardinal size={13} weight="bold" className="text-indigo-300" />
          Arrange
        </span>
        <span className="flex items-center gap-1" data-testid="arrange-snap-modes">
          <Magnet size={12} weight="bold" className="text-zinc-500" />
          {SNAP_MODES.map(([k, label]) => (
            <Tb
              key={k}
              testId={`arrange-snap-${k}`}
              title={label}
              on={snapMode === k}
              onClick={() => onSnapMode(k)}
            >
              {label}
            </Tb>
          ))}
        </span>
        {connected && (
          <span
            data-testid="arrange-connected"
            className="flex h-7 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 text-[10px] font-bold uppercase tracking-wider text-emerald-200"
          >
            <LinkSimple size={12} weight="bold" /> Connected · {connected}
          </span>
        )}
        {seams.length > 0 && (
          <span className="flex flex-wrap items-center gap-1" data-testid="arrange-seams">
            {seams.slice(0, 4).map((s, i) => {
              const bad = (s.issues || []).length > 0;
              return (
                <span
                  key={s.id}
                  data-testid={`arrange-seam-${i}`}
                  data-flagged={bad ? "true" : "false"}
                  title={bad ? s.issues.join("\n") : `Seam between ${s.label}`}
                  className={`flex h-7 items-center gap-1 rounded-full border px-2.5 text-[10px] font-bold uppercase tracking-wider ${
                    bad
                      ? "border-red-500/60 bg-red-500/20 text-red-200"
                      : "border-amber-500/40 bg-amber-500/15 text-amber-200"
                  }`}
                >
                  {bad ? <Warning size={12} weight="bold" /> : <Ruler size={12} weight="bold" />}
                  Seam {fmtIn(s.len)}
                  <span className="hidden normal-case tracking-normal opacity-70 lg:inline">
                    · {bad ? s.issues[0] : s.label}
                  </span>
                </span>
              );
            })}
            {seams.length > 4 && (
              <span className="text-[10px] text-amber-200/70" data-testid="arrange-seams-more">
                +{seams.length - 4} more
              </span>
            )}
          </span>
        )}
        {hasOverrides && (
          <>
            <Tb
              testId="arrange-apply"
              title="Write these positions into the drawing and slab layout"
              onClick={onApply}
              className="!border-emerald-500/40 !bg-emerald-500/15 !text-emerald-200 hover:!bg-emerald-500/25"
            >
              <Check size={12} weight="bold" /> Apply to drawing
            </Tb>
            <Tb
              testId="arrange-reset-all"
              title="Put every piece back where the drawing has it"
              onClick={onResetAll}
            >
              <ArrowUUpLeft size={12} weight="bold" /> Reset all
            </Tb>
          </>
        )}
      </div>
      {piece ? (
        <div
          data-testid="arrange-piece-bar"
          className="flex flex-wrap items-center gap-1.5 rounded-2xl border border-indigo-500/30 bg-zinc-950/85 p-2 shadow-xl backdrop-blur"
        >
          <span
            data-testid="arrange-selected-name"
            className="max-w-[160px] truncate pl-1 text-[11px] font-semibold text-indigo-100"
          >
            {pieceName}
          </span>
          {upright ? (
            <>
              <span className="flex items-center gap-1" data-testid="arrange-stand">
                <Tb
                  testId="arrange-stand-backsplash"
                  on={piece.stand !== "waterfall"}
                  onClick={() => onStand("backsplash")}
                  title="Stands up off the counter top"
                >
                  Backsplash
                </Tb>
                <Tb
                  testId="arrange-stand-waterfall"
                  on={piece.stand === "waterfall"}
                  onClick={() => onStand("waterfall")}
                  title="Drops from the counter to the floor"
                >
                  Waterfall
                </Tb>
              </span>
              <span
                data-testid="arrange-attach-status"
                className="text-[10px] uppercase tracking-wider text-zinc-400"
              >
                {piece.attach?.piece_id
                  ? `On ${attachName} · side ${sideLetter(piece.attach.edge)}`
                  : "Drag it onto a counter side to attach"}
              </span>
              {piece.attach?.piece_id && (
                <Tb testId="arrange-detach" onClick={onDetach} title="Let it find the nearest side again">
                  Detach
                </Tb>
              )}
            </>
          ) : (
            <>
              <span className="flex items-center gap-1" data-testid="arrange-rotate">
                <Tb testId="arrange-rotate--90" title="Rotate 90° counter-clockwise" onClick={() => onRotate(-90)}>
                  <ArrowCounterClockwise size={12} weight="bold" /> 90
                </Tb>
                <Tb testId="arrange-rotate-90" title="Rotate 90° clockwise" onClick={() => onRotate(90)}>
                  <ArrowClockwise size={12} weight="bold" /> 90
                </Tb>
                <Tb testId="arrange-rotate-45" title="Rotate 45°" onClick={() => onRotate(45)}>
                  45
                </Tb>
                <Tb testId="arrange-rotate-15" title="Rotate 15°" onClick={() => onRotate(15)}>
                  15
                </Tb>
                <label className="flex h-7 items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900/70 px-2 text-[10px] font-bold text-zinc-400">
                  <input
                    type="number"
                    step="1"
                    data-testid="arrange-rot-input"
                    value={Math.round(Number(piece.rot) || 0)}
                    onChange={(e) => onSetRot(e.target.value)}
                    className="w-11 bg-transparent text-right font-mono-data text-[11px] text-zinc-100 outline-none"
                  />
                  °
                </label>
              </span>
              <span className="flex items-center gap-1" data-testid="arrange-flip">
                <Tb testId="arrange-flip-h" title="Flip left ↔ right" onClick={() => onFlip("h")}>
                  <FlipHorizontal size={12} weight="bold" /> Flip
                </Tb>
                <Tb testId="arrange-flip-v" title="Flip front ↔ back" onClick={() => onFlip("v")}>
                  <FlipVertical size={12} weight="bold" /> Flip
                </Tb>
              </span>
              <span className="flex items-center gap-0.5" data-testid="arrange-nudge">
                <Tb testId="arrange-nudge-left" title="Nudge 1″ left (Shift = 12″)" onClick={(e) => onNudge(e.shiftKey ? -12 : -1, 0)} className="!px-1.5">
                  <ArrowLeft size={12} weight="bold" />
                </Tb>
                <Tb testId="arrange-nudge-up" title="Nudge 1″ back" onClick={(e) => onNudge(0, e.shiftKey ? -12 : -1)} className="!px-1.5">
                  <ArrowUp size={12} weight="bold" />
                </Tb>
                <Tb testId="arrange-nudge-down" title="Nudge 1″ forward" onClick={(e) => onNudge(0, e.shiftKey ? 12 : 1)} className="!px-1.5">
                  <ArrowDown size={12} weight="bold" />
                </Tb>
                <Tb testId="arrange-nudge-right" title="Nudge 1″ right" onClick={(e) => onNudge(e.shiftKey ? 12 : 1, 0)} className="!px-1.5">
                  <ArrowRight size={12} weight="bold" />
                </Tb>
              </span>
              <span className="flex items-center gap-0.5" data-testid="arrange-lift">
                <Tb testId="arrange-lift-down" title="Lower 1″ (Shift = 6″)" onClick={(e) => onLift(e.shiftKey ? -6 : -1)} className="!px-1.5">
                  <ArrowLineDown size={12} weight="bold" />
                </Tb>
                <label className="flex h-7 items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900/70 px-2 text-[10px] font-bold text-zinc-400" title="Height above standard counter">
                  <input
                    type="number"
                    step="1"
                    data-testid="arrange-lift-input"
                    value={Math.round((Number(piece.lift) || 0) * 10) / 10}
                    onChange={(e) => onSetLift(e.target.value)}
                    className="w-10 bg-transparent text-right font-mono-data text-[11px] text-zinc-100 outline-none"
                  />
                  ″ up
                </label>
                <Tb testId="arrange-lift-up" title="Raise 1″ (Shift = 6″)" onClick={(e) => onLift(e.shiftKey ? 6 : 1)} className="!px-1.5">
                  <ArrowLineUp size={12} weight="bold" />
                </Tb>
              </span>
            </>
          )}
          <Tb testId="arrange-reset-piece" title="Back to where the drawing has this piece" onClick={onResetPiece}>
            <ArrowUUpLeft size={12} weight="bold" /> Reset
          </Tb>
          <span className="hidden pl-1 text-[10px] text-zinc-500 md:block" data-testid="arrange-hint">
            {upright
              ? "Drag the panel onto any counter side"
              : "Drag to move · dial to spin · arrow above lifts · PgUp/PgDn · R rotate · F flip"}
          </span>
        </div>
      ) : (
        <span
          data-testid="arrange-hint"
          className="w-fit rounded-full border border-zinc-800 bg-zinc-950/70 px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-500 backdrop-blur"
        >
          Click a piece to move, rotate or flip it
        </span>
      )}
    </div>
  );
};
